import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Ban, Bell, Check, ChefHat, ChevronRight, Flame, Hourglass, PackageX, Radio, ShieldAlert, X } from "lucide-react";
import { realtime } from "@/lib/realtime";
import { putOne, readOne } from "@/lib/localDb";

/**
 * Session 63.E — Owner / Manager NOTIFICATIONS (replaces the "Local activity"
 * strip the owner found to be an eyesore).
 *
 * Every live event the manager should know about becomes ONE card in a
 * sticky stack at the top of the dashboard:
 *
 *   • tap the card           → jump straight to the screen that deals with it
 *   • tap the ✕ ("Noted")    → the card is gone for good
 *
 * Sources (unified realtime bus — cloud WS or LAN mesh):
 *   voids · prep sheet lifecycle · production confirmed · fallback approvals
 *   requested / decided · sale blocked (out of stock) · fallback / shortfall
 *   stock events · NEW: order waiting too long (`order_long_wait`).
 *
 * Cards persist per business in IndexedDB so a reload (even offline) keeps
 * what has not been noted yet. Nothing here writes to the server except the
 * approval decisions.
 */
const KEY_PREFIX = "notifications:owner:";
const LEGACY_KEYS = ["activity:owner", "activity:owner:"];
const MAX = 60;
const VISIBLE = 3;
const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/\.?0+$/, "")));

/** Translate a realtime message into a card. `target` is the deep-link key. */
function describe(msg) {
  const t = String(msg?.type || "");
  const p = msg?.payload || {};
  // Audit-log echoes reuse the action name with a {target_type, target_id,
  // user} payload — the rich domain event arrives separately. Skip echoes.
  if (p.target_type && !p.sheet_id && !p.approval && !p.station && !p.blocked) return null;
  if (t === "order_status_changed" && p.voided === true) {
    const lines = Array.isArray(p.voided_lines) ? p.voided_lines : [];
    const what = lines.length
      ? lines.map((l) => `${l.quantity || 1}× ${l.name || "item"}`).join(", ")
      : (p.full_void ? "whole order" : `${(p.voided_item_ids || []).length || ""} line(s)`);
    const value = Number(p.voided_value) > 0 ? ` · R ${Number(p.voided_value).toFixed(2)}` : "";
    return {
      icon: "void", tone: "void",
      title: `${p.full_void ? "Order voided" : "Items voided"} — Table ${p.table_number || "—"}${p.order_number ? ` · ${p.order_number}` : ""}`,
      body: `${what}${value}${p.voided_by ? ` · by ${p.voided_by}` : ""}${p.void_reason ? ` · “${p.void_reason}”` : ""}`,
      target: { key: "order", order_number: p.order_number || null, order_id: p.order_id || null, table_number: p.table_number || null },
    };
  }
  if (t === "order_long_wait") {
    const items = Array.isArray(p.items) ? p.items : [];
    const stations = Array.isArray(p.stations) && p.stations.length ? p.stations.map((s) => (s === "bar" ? "Bar" : s === "kitchen" ? "Kitchen" : s)).join(" + ") : "Station";
    return {
      icon: "wait", tone: "warn",
      title: `Order waiting too long — Table ${p.table_number || "—"}${p.order_number ? ` · ${p.order_number}` : ""}`,
      body: `${p.waiting_minutes != null ? `${p.waiting_minutes} min` : "Over the limit"}${p.threshold_minutes ? ` (limit ${p.threshold_minutes} min)` : ""} · ${stations} · ${items.slice(0, 3).map((it) => `${it.quantity || 1}× ${it.name || "item"}`).join(", ")}${items.length > 3 ? ` +${items.length - 3} more` : ""}`,
      target: { key: "live-orders", order_number: p.order_number || null, order_id: p.order_id || null, table_number: p.table_number || null },
    };
  }
  const st = p.station === "bar" ? "Bar" : p.station === "kitchen" ? "Kitchen" : (p.station || "Station");
  switch (t) {
    case "prep_sheet_sent": return { icon: "prep", tone: "info", title: `${st} prep sheet sent`, body: `${p.title || ""}${p.by ? ` · by ${p.by}` : ""}`, target: { key: "prep" } };
    case "prep_sheet_in_progress": return { icon: "prep", tone: "info", title: `${st} started prep`, body: `${p.title || ""}${p.by ? ` · ${p.by}` : ""}`, target: { key: "prep" } };
    case "prep_progress":
    case "prep_progress_recorded":
      return null; // line-by-line ticks are noise for a notification stack
    case "prep_sheet_submitted": {
      const s = p.summary || {};
      const v = Array.isArray(p.variance) && p.variance.length ? ` · ${p.variance.length} line${p.variance.length === 1 ? "" : "s"} differ from target` : "";
      return { icon: "prep", tone: "action", title: `${st} submitted prep — confirm production`, body: `${p.title || ""} · ${s.prepared ?? "?"} prepared, ${s.unable ?? 0} unable${v}${p.by || p.user ? ` · ${p.by || p.user}` : ""}`, target: { key: "prep" } };
    }
    case "prep_sheet_approved": return { icon: "prep", tone: "info", title: "Prep sheet approved", body: `${p.title || ""}${p.by ? ` · ${p.by}` : ""}`, target: { key: "prep" } };
    case "prep_sheet_cancelled": return { icon: "prep", tone: "warn", title: "Prep sheet cancelled", body: `${p.title || ""}${p.by ? ` · ${p.by}` : ""}`, target: { key: "prep" } };
    case "production_confirmed": return { icon: "production", tone: "ok", title: "Production confirmed", body: `${p.title || ""}${p.by || p.user ? ` · by ${p.by || p.user}` : ""} · stock updated (PRODUCTION)`, target: { key: "prep" } };
    case "fallback_approval_requested": { const a = p.approval || {}; return { icon: "approval", tone: "action", title: `Approval needed: ${a.ingredient_name || "ingredient"} × ${a.quantity_label || `${fmt(a.quantity)} ${a.unit || ""}`}`, body: `${a.menu_item_name ? `${a.menu_item_name} · ` : ""}Table ${a.table_number || "—"} · ${a.staff_name || "staff"} · service stock ${fmt(a.preferred_balance)}, fallback ${fmt(a.fallback_available)}`, target: { key: "approvals" } }; }
    case "fallback_approval_decided": { const a = p.approval || {}; return { icon: "approval", tone: a.status === "approved" ? "ok" : "warn", title: `Fallback ${a.status === "approved" ? "allowed" : a.status}: ${a.ingredient_name || ""}`, body: `Table ${a.table_number || "—"} · ${a.decided_by || ""}${a.decision_method === "device_password" ? " (on device)" : ""}`, target: { key: "approvals" } }; }
    case "stock_attempt_rejected": { const b = (p.blocked || [])[0] || {}; const needsManager = p.code === "manager_approval_required"; return { icon: "oos", tone: needsManager ? "action" : "warn", title: needsManager ? "Sale blocked — manager approval required" : "Sale blocked — out of stock at service location", body: `${(p.items || []).join(", ")}${b.ingredient_name ? ` · ${b.ingredient_name} need ${b.required_label || `${fmt(b.required)} ${b.unit || ""}`}` : ""}${p.actor ? ` · ${p.actor}` : ""} · nothing was ordered`, target: { key: needsManager ? "approvals" : "fallback" } }; }
    case "stock_event": {
      const kind = String(p.type || "");
      if (kind === "out_of_stock_attempt") return null; // already covered by stock_attempt_rejected
      return { icon: "stock", tone: kind === "shortfall" ? "warn" : "info", title: kind === "shortfall" ? `Shortfall: ${p.ingredient_name || ""}` : kind === "fallback" ? `Fallback stock used: ${p.ingredient_name || ""}` : `Stock event: ${p.ingredient_name || kind}`, body: `${p.quantity != null ? `${p.quantity_label || `${fmt(p.quantity)} ${p.unit || ""}`} · ` : ""}${p.preferred_location_name ? `service location ${p.preferred_location_name}` : ""}${p.source_location_name ? ` ← ${p.source_location_name}` : ""}`, target: { key: "fallback" } };
    }
    default: return null;
  }
}

/** Same action seen twice (LAN ping + cloud echo, or a replayed envelope) → one card. */
function dedupeKey(msg) {
  const t = String(msg?.type || "");
  const p = msg?.payload || {};
  if (p.approval && p.approval.id) return `${t}:${p.approval.id}:${p.approval.status || ""}`;
  if (p.sheet_id) return `${t}:${p.sheet_id}:${p.status || ""}`;
  if (t === "stock_attempt_rejected") return `${t}:${p.table_session_id || ""}:${(p.items || []).join(",")}:${String(msg.ts || "").slice(0, 16)}`;
  if (t === "order_status_changed" && p.voided === true) return `void:${p.order_id || ""}:${(p.voided_item_ids || []).join(",")}:${p.at || msg.ts || ""}`;
  if (t === "order_long_wait") return `longwait:${p.order_id || p.order_number || ""}`;
  return msg.change_id || `${t}:${msg.ts || ""}:${JSON.stringify(p).slice(0, 80)}`;
}

const TONE = {
  info: "border-border bg-card hover:bg-secondary/60",
  ok: "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10",
  warn: "border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/15",
  action: "border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10 hover:bg-[hsl(var(--accent))]/15",
  void: "border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10",
};
const ICON_TONE = {
  info: "bg-secondary text-foreground",
  ok: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  warn: "bg-amber-500/20 text-amber-800 dark:text-amber-200",
  action: "bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]",
  void: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
};
const ICON = { prep: ChefHat, production: Flame, approval: ShieldAlert, oos: PackageX, stock: AlertTriangle, void: Ban, wait: Hourglass };
const TARGET_LABEL = { order: "Open order", "live-orders": "Open live orders", prep: "Open prep sheets", approvals: "Open approvals", fallback: "Open fallback activity", "stock-history": "Open stock history" };

export const OwnerNotifications = ({ http, authHeaders, businessId = null, canApprove = true, onNavigate, enabled = true }) => {
  const KEY = businessId ? `${KEY_PREFIX}${businessId}` : null;
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [pending, setPending] = useState(null); // pending approvals from server (reconciliation)
  const [busy, setBusy] = useState("");
  const seen = useRef(new Set());

  // Restore persisted cards for THIS business (and reset when it changes).
  useEffect(() => {
    setRows([]); setPending([]); seen.current = new Set();
    if (!KEY) return;
    (async () => {
      for (const k of LEGACY_KEYS) { try { await putOne("meta", { key: k, data: [], ts: new Date().toISOString() }); } catch (_) { /* noop */ } }
      try { const r = await readOne("meta", KEY); if (r && Array.isArray(r.data)) { setRows(r.data); r.data.forEach((x) => seen.current.add(x.id)); } } catch (_) { /* noop */ }
    })();
  }, [KEY]);
  const persist = useCallback((next) => { if (!KEY) return; try { putOne("meta", { key: KEY, data: next.slice(0, MAX), ts: new Date().toISOString() }); } catch (_) { /* noop */ } }, [KEY]);

  const loadPending = useCallback(async () => {
    if (!http || !authHeaders) return;
    try { const r = await http.get("/owner/fallback-approvals", { headers: authHeaders, params: { status: "pending", limit: 20 } }); setPending(r.data?.approvals || []); } catch (_) { /* offline: keep last */ }
  }, [http, authHeaders]);
  useEffect(() => { if (enabled) loadPending(); }, [loadPending, enabled]);
  useEffect(() => { if (!enabled) return undefined; const iv = setInterval(loadPending, 30000); return () => clearInterval(iv); }, [loadPending, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsub = realtime.subscribe((msg) => {
      // Ignore anything tagged for another business (mesh peers / stale sockets).
      const mb = msg && (msg.business_id || (msg.payload && msg.payload.business_id));
      if (businessId && mb && String(mb) !== String(businessId)) return;
      if (/^fallback_approval/.test(msg.type)) loadPending();
      if (msg.type === "fallback_approval_requested" && msg.payload?.approval) setPending((cur) => { const a = msg.payload.approval; const list = (cur || []).filter((x) => x.id !== a.id); return [a, ...list]; });
      if (msg.type === "fallback_approval_decided" && msg.payload?.approval) setPending((cur) => (cur || []).filter((x) => x.id !== msg.payload.approval.id));
      const d = describe(msg);
      if (!d) return;
      const id = dedupeKey(msg);
      if (seen.current.has(id)) return;
      seen.current.add(id);
      const row = { id, type: msg.type, ts: msg.ts || new Date().toISOString(), via: msg._via === "mesh" ? "lan" : "cloud", ...d };
      setRows((cur) => { const next = [row, ...cur.filter((x) => x.id !== id)].slice(0, MAX); persist(next); return next; });
    });
    return unsub;
  }, [enabled, persist, loadPending, businessId]);

  const decide = async (a, action, switchPolicy = false) => {
    setBusy(a.id + action + (switchPolicy ? "+switch" : ""));
    try {
      await http.post(`/owner/fallback-approvals/${a.id}/decide`, { action, switch_policy: !!switchPolicy }, { headers: authHeaders });
      if (switchPolicy) toast.success(`Approved — ${a.ingredient_name || "ingredient"} now uses "Service stock first, then fallback" for the rest of service.`, { description: "Reset it under Inventory → Fallback history at the end of the day." });
      else toast.success(action === "allow" ? `Allowed — ${a.staff_name || "staff"} can add the item again now.` : "Rejected.");
      setPending((cur) => (cur || []).filter((x) => x.id !== a.id));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || (navigator.onLine === false ? "You're offline — approve on the staff device with the manager password instead." : "Couldn't record the decision."));
    } finally { setBusy(""); }
  };

  const dismiss = useCallback((id) => {
    setRows((cur) => { const next = cur.filter((r) => r.id !== id); persist(next); return next; });
  }, [persist]);
  const dismissAll = () => { setRows([]); persist([]); setShowAll(false); };
  const open = (r) => {
    try { if (onNavigate) onNavigate(r.target || { key: "overview" }, r); } catch (_) { /* noop */ }
    dismiss(r.id);
  };

  const visible = useMemo(() => (showAll ? rows : rows.slice(0, VISIBLE)), [rows, showAll]);
  const hiddenCount = Math.max(0, rows.length - visible.length);
  if (!enabled) return null;
  if (!(pending || []).length && rows.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="owner-notifications">
      {/* Pending approvals — the one thing a manager must act on. */}
      {(pending || []).length ? (
        <div className="rounded-[24px] border border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10 p-4 shadow-md backdrop-blur-md" data-testid="pending-approvals-banner">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            <p className="font-semibold">{pending.length} fallback approval{pending.length === 1 ? "" : "s"} waiting</p>
            <p className="text-xs text-muted-foreground">Allow once = this request only. Approve = also switch the ingredient to “Service stock first, then fallback” for today. Staff must add the item again (valid 15 min).</p>
            {onNavigate ? <Button variant="ghost" className="ml-auto h-9 rounded-xl" onClick={() => onNavigate({ key: "approvals" })} data-testid="pending-approvals-open">All approvals →</Button> : null}
          </div>
          <div className="mt-3 space-y-2">
            {pending.slice(0, 3).map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2" data-testid={`pending-approval-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{a.ingredient_name} × {a.quantity_label || `${fmt(a.quantity)} ${a.unit || ""}`}</p>
                  <p className="text-xs text-muted-foreground">{a.menu_item_name ? `${a.menu_item_name} · ` : ""}Table {a.table_number || "—"} · {a.staff_name} · service {fmt(a.preferred_balance)} · fallback {fmt(a.fallback_available)}</p>
                </div>
                {canApprove ? (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="h-11 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" disabled={!!busy} onClick={() => decide(a, "reject")} data-testid={`pending-approval-reject-${a.id}`}><X className="h-4 w-4" /> Reject</Button>
                    <Button variant="outline" className="h-11 rounded-xl" disabled={!!busy} onClick={() => decide(a, "allow")} data-testid={`pending-approval-allow-${a.id}`}><Check className="h-4 w-4" /> Allow once</Button>
                    <Button className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={!!busy} onClick={() => decide(a, "allow", true)} title="Allow AND switch this ingredient to “Service stock first, then fallback” for the rest of service" data-testid={`pending-approval-approve-switch-${a.id}`}><Check className="h-4 w-4" /> Approve</Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Notification cards — tap to go there, ✕ to note it away. */}
      {rows.length ? (
        <div className="space-y-2" data-testid="owner-notification-list">
          {visible.map((r) => {
            const I = ICON[r.icon] || Bell;
            const targetLabel = TARGET_LABEL[r.target?.key] || "Open";
            return (
              <div
                key={r.id}
                className={`relative flex items-start gap-3 rounded-[22px] border p-3 pr-14 shadow-md backdrop-blur-md transition-colors ${TONE[r.tone] || TONE.info}`}
                data-testid={`owner-notification-${r.type}`}
              >
                <button
                  type="button"
                  onClick={() => open(r)}
                  className="flex min-w-0 flex-1 items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
                  title={targetLabel}
                  data-testid="owner-notification-open"
                >
                  <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${ICON_TONE[r.tone] || ICON_TONE.info}`}>
                    <I className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold leading-tight">{r.title}</span>
                      <span className="text-[11px] tabular-nums text-muted-foreground">{new Date(r.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      {r.via === "lan" ? <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase text-[hsl(var(--primary))]"><Radio className="h-3 w-3" /> LAN</span> : null}
                    </span>
                    {r.body ? <span className="mt-0.5 block truncate text-xs text-muted-foreground">{r.body}</span> : null}
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline">{targetLabel} <ChevronRight className="h-3 w-3" /></span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(r.id)}
                  className="absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Noted — dismiss this notification"
                  title="Noted"
                  data-testid="owner-notification-dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          {rows.length > 1 || hiddenCount ? (
            <div className="flex flex-wrap items-center gap-2 px-1">
              {hiddenCount ? (
                <Button variant="outline" className="h-9 rounded-xl text-xs" onClick={() => setShowAll(true)} data-testid="owner-notifications-show-all">
                  <Bell className="h-3.5 w-3.5" /> {hiddenCount} more
                </Button>
              ) : showAll && rows.length > VISIBLE ? (
                <Button variant="outline" className="h-9 rounded-xl text-xs" onClick={() => setShowAll(false)} data-testid="owner-notifications-show-less">Show fewer</Button>
              ) : null}
              <Badge variant="outline" className="rounded-full text-[11px]">{rows.length} notification{rows.length === 1 ? "" : "s"}</Badge>
              <Button variant="ghost" className="ml-auto h-9 rounded-xl text-xs text-muted-foreground" onClick={dismissAll} data-testid="owner-notifications-dismiss-all">
                <Check className="h-3.5 w-3.5" /> Noted all
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
